import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDeprecateReleaseMutation } from '../rtk/api/datagroupsApi';
import {
  normalizePlan,
  pruneSelection,
  hasSelectionDrifted,
  buildDeprecatePayload,
  isValidEmail,
  filterEntities,
  countOrphans,
  canExecuteRelease,
  mergeSelection,
} from '../utils/release/deprecationPlan';
import { formatApiError } from '../utils/release/apiError';

/**
 * All interdependent state for the deprecate-release flow lives in this reducer
 * instead of a dozen useState hooks. The component using this hook stays a thin
 * orchestrator.
 *
 * step: 'form' (collect justification) → 'review' (dry-run plan) → execute.
 */
const initialState = {
  step: 'form',
  reason: '',
  confirmVersion: '',
  // Explicit acknowledgement that orphaned entities will lose their Latest
  // version. Only required (and only surfaced) when the plan has orphan risks.
  orphanAck: false,
  activeTab: 'all',
  search: '',
  showOrphansOnly: false,
  plan: null,
  errorMessage: '',
  // PKs the user selects to inactivate (Operation 2). Empty = untag all (Op 1).
  selectedPks: [],
  // The selection the currently displayed plan was generated for, so we can
  // detect drift after previewing.
  previewedPks: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ERROR':
      return { ...state, errorMessage: action.message };
    case 'TOGGLE_VISIBLE':
      return {
        ...state,
        selectedPks: mergeSelection(state.selectedPks, action.pks, action.checked),
      };
    case 'PLAN_READY': {
      // Prune selection to entities that actually exist in the new plan, so a
      // stale PK is never sent as a targetEntity (backend would 400).
      const pruned = pruneSelection(action.targets, action.plan);
      return {
        ...state,
        plan: action.plan,
        selectedPks: pruned,
        previewedPks: [...pruned],
        // A fresh plan may change which entities are orphaned, so the prior
        // acknowledgement no longer applies.
        orphanAck: false,
        step: 'review',
        errorMessage: '',
      };
    }
    case 'GO_TO_FORM':
      return { ...state, step: 'form' };
    default:
      return state;
  }
}

export function useDeprecateFlow({
  groupName,
  releaseVersion,
  email,
  open,
  onDeprecated,
  onClose,
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [deprecateRelease, { isLoading }] = useDeprecateReleaseMutation();

  const trimmedEmail = (email || '').trim();

  // Reset transient state whenever the modal opens for a (new) release. Email is
  // intentionally not a dependency: a token refresh that changes the email
  // string must not wipe the user's in-progress reason/selection.
  useEffect(() => {
    if (open) dispatch({ type: 'RESET' });
  }, [open, releaseVersion]);

  const setField = useCallback((field, value) => dispatch({ type: 'SET_FIELD', field, value }), []);
  const toggleVisible = useCallback(
    (pks, checked) => dispatch({ type: 'TOGGLE_VISIBLE', pks, checked }),
    []
  );
  const goToForm = useCallback(() => dispatch({ type: 'GO_TO_FORM' }), []);

  // Run a dry-run for the given selection and record which selection it
  // reflects, so the reviewed plan always matches what would be executed.
  const runDryRun = useCallback(
    async (targets) => {
      dispatch({ type: 'SET_ERROR', message: '' });
      try {
        const result = await deprecateRelease({
          groupName,
          releaseVersion,
          payload: buildDeprecatePayload({ email: trimmedEmail, reason: state.reason, targets }),
          dryRun: true,
        }).unwrap();
        dispatch({ type: 'PLAN_READY', plan: normalizePlan(result), targets });
      } catch (err) {
        console.error('[Deprecate] dry-run failed', err);
        dispatch({ type: 'SET_ERROR', message: formatApiError(err, 'Dry-run failed') });
      }
    },
    [deprecateRelease, groupName, releaseVersion, trimmedEmail, state.reason]
  );

  const execute = useCallback(async () => {
    dispatch({ type: 'SET_ERROR', message: '' });
    try {
      const result = await deprecateRelease({
        groupName,
        releaseVersion,
        payload: buildDeprecatePayload({
          email: trimmedEmail,
          reason: state.reason,
          targets: state.previewedPks,
        }),
        dryRun: false,
      }).unwrap();
      onDeprecated?.(result);
      onClose?.();
    } catch (err) {
      console.error('[Deprecate] execute failed', err);
      dispatch({ type: 'SET_ERROR', message: formatApiError(err, 'Failed to deprecate release') });
    }
  }, [
    deprecateRelease,
    groupName,
    releaseVersion,
    trimmedEmail,
    state.reason,
    state.previewedPks,
    onDeprecated,
    onClose,
  ]);

  // ── Derived data ──────────────────────────────────────────────────
  const filteredEntities = useMemo(
    () =>
      filterEntities(state.plan?.entities, {
        activeTab: state.activeTab,
        search: state.search,
        showOrphansOnly: state.showOrphansOnly,
      }),
    [state.plan, state.activeTab, state.search, state.showOrphansOnly]
  );

  const emailValid = isValidEmail(trimmedEmail);
  const canRunDryRun = emailValid && state.reason.trim().length > 0;
  const selectionDrifted = hasSelectionDrifted(state.selectedPks, state.previewedPks);
  const orphanCount = countOrphans(state.plan?.entities);
  // When orphans exist the user must explicitly acknowledge the data loss.
  const orphanAckRequired = orphanCount > 0;
  const canExecute = canExecuteRelease({
    emailValid,
    confirmVersion: state.confirmVersion,
    releaseVersion,
    isLoading,
    orphanAckRequired,
    orphanAck: state.orphanAck,
    selectionDrifted,
  });

  return {
    state,
    isLoading,
    // setters / actions
    setField,
    toggleVisible,
    goToForm,
    runDryRun,
    execute,
    // derived
    filteredEntities,
    emailValid,
    canRunDryRun,
    selectionDrifted,
    canExecute,
    orphanCount,
    orphanAckRequired,
  };
}
